import React, { useCallback, useEffect, useState } from "react";
import { logix2ReasoningApi, logix2HitlApi } from "@/api/oraclePhase28Client";
import ComplianceLock from "@/components/compliance/ComplianceLock";

/**
 * CareLogix™ ORACLE — LOGIX-2 Multi-Agent Reasoning Panel (Phase 28D).
 *
 * Free-form reasoning beyond the structured ORACLE SCORE™. Surfaces the
 * trace of 4 virtual agents (Market Analyst, Risk Officer, Compliance
 * Officer, Synthesizer). All output is HITL-gated.
 */

const VERDICT_COLORS = {
  watch_strong: "#10b981",
  watch:        "#22c55e",
  consider:     "#fcd34d",
  hold:         "#94a3b8",
  avoid:        "#ef4444",
};

const STATUS_COLORS = {
  pending:    "#fcd34d",
  approved:   "#10b981",
  rejected:   "#ef4444",
  escalated:  "#a78bfa",
};

function AgentBlock({ msg, testid }) {
  return (
    <article style={S.agent} data-testid={testid}>
      <header style={S.agentHead}>
        <span style={{ ...S.agentChip, color: msg.color, borderColor: msg.color, background: `${msg.color}1a` }}>
          {msg.label}
        </span>
        {msg.offline && (
          <span style={S.offlinePill} data-testid={`${testid}-offline`} title={msg.error || ""}>
            offline fallback
          </span>
        )}
        <time style={S.agentTime}>{(msg.timestamp || "").slice(11, 19)}</time>
      </header>
      <pre style={S.agentBody}>{msg.text}</pre>
    </article>
  );
}

export default function Logix2ReasoningPanel({
  symbol = "AAPL",
  assetClass = "stocks",
  scannerType,
  isAdmin = false,
  testid = "logix2-reasoning-panel",
}) {
  const [trace, setTrace] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");
  const [hitlBusy, setHitlBusy] = useState("");

  const start = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await logix2ReasoningApi.reason({
        symbol,
        asset_class: assetClass,
        user_query: query,
        scanner_type: scannerType,
      });
      setTrace(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "reason_failed");
    } finally { setBusy(false); }
  }, [symbol, assetClass, query, scannerType]);

  useEffect(() => {
    // Auto-kick a baseline trace once on mount so the user sees something.
    if (!trace && !busy) start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runHitl = useCallback(async (action) => {
    if (!trace?.trace_id || !isAdmin) return;
    setHitlBusy(action);
    try {
      const op = logix2HitlApi[action];
      if (!op) return;
      const { data } = await op(trace.trace_id, "");
      setTrace((t) => ({ ...t, ...data }));
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || `${action}_failed`);
    } finally { setHitlBusy(""); }
  }, [trace, isAdmin]);

  const verdictColor = VERDICT_COLORS[trace?.verdict] || VERDICT_COLORS.consider;
  const statusColor = STATUS_COLORS[trace?.status] || STATUS_COLORS.pending;

  return (
    <section style={S.shell} data-testid={testid}>
      <header style={S.head}>
        <div>
          <span style={S.tagline}>LOGIX-2™ Multi-Agent Reasoning · HITL-gated</span>
          <h3 style={S.title}>
            Free-form deliberation for <code>{symbol}</code>{" "}
            <small style={{ color: "#94a3b8" }}>{assetClass}</small>
          </h3>
        </div>
        <div style={S.headerActions}>
          {trace?.verdict && (
            <span style={{ ...S.pill, color: verdictColor, borderColor: verdictColor, background: `${verdictColor}1a` }} data-testid={`${testid}-verdict`}>
              {trace.verdict.toUpperCase().replace(/_/g, " ")}
            </span>
          )}
          {trace?.status && (
            <span style={{ ...S.pill, color: statusColor, borderColor: statusColor, background: `${statusColor}1a` }} data-testid={`${testid}-status`}>
              {trace.status.toUpperCase()}
            </span>
          )}
          <button type="button" onClick={start} disabled={busy} style={S.refresh} data-testid={`${testid}-rerun`}>
            {busy ? "…" : "↻ Re-run"}
          </button>
        </div>
      </header>

      <div style={S.queryRow}>
        <input
          type="text"
          placeholder="Optional question for LOGIX-2 (e.g., why is the trend score low?)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={S.queryInput}
          data-testid={`${testid}-query`}
        />
        <button type="button" onClick={start} disabled={busy} style={S.primaryBtn} data-testid={`${testid}-ask`}>
          {busy ? "Thinking…" : "Ask LOGIX-2"}
        </button>
      </div>

      {err && <div style={S.err} data-testid={`${testid}-error`}>⚠ {err}</div>}

      {trace?.agents?.length > 0 && (
        <div style={S.agents} data-testid={`${testid}-agents`}>
          {trace.agents.map((m, i) => (
            <AgentBlock key={`${trace.trace_id}-${m.agent}`} msg={m} testid={`${testid}-agent-${m.agent}`} />
          ))}
        </div>
      )}

      {isAdmin && trace?.trace_id && (
        <div style={S.hitlRow} data-testid={`${testid}-hitl`}>
          <button type="button" disabled={hitlBusy !== "" || trace.status === "approved"} onClick={() => runHitl("approve")} style={S.approveBtn} data-testid={`${testid}-approve`}>
            ✓ Approve
          </button>
          <button type="button" disabled={hitlBusy !== "" || trace.status === "rejected"} onClick={() => runHitl("reject")} style={S.rejectBtn} data-testid={`${testid}-reject`}>
            ✕ Reject
          </button>
          <button type="button" disabled={hitlBusy !== "" || trace.status === "escalated"} onClick={() => runHitl("escalate")} style={S.escalateBtn} data-testid={`${testid}-escalate`}>
            ⚑ Escalate
          </button>
          <span style={S.traceCode} title="Trace ID">{trace.trace_id.slice(0, 12)}…</span>
        </div>
      )}

      <ComplianceLock variant="inline" short policy="v27" testid={`${testid}-compliance`} />
    </section>
  );
}

const S = {
  shell: { background: "linear-gradient(135deg, rgba(11,15,20,0.96), rgba(8,32,22,0.92))", border: "1px solid rgba(197,160,89,0.30)", borderRadius: 14, padding: 14, color: "#e2e8f0", display: "flex", flexDirection: "column", gap: 10, fontFamily: "'Figtree', 'Inter', system-ui, sans-serif", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" },
  tagline: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.18em" },
  title: { color: "#fde68a", margin: 0, fontSize: 16 },
  headerActions: { display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" },
  pill: { fontFamily: "'Space Mono', monospace", fontSize: 10.5, fontWeight: 800, letterSpacing: "0.10em", padding: "4px 10px", border: "1px solid", borderRadius: 999 },
  refresh: { appearance: "none", background: "transparent", border: "1px solid rgba(197,160,89,0.45)", color: "#c5a059", padding: "5px 11px", borderRadius: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800, letterSpacing: "0.06em" },
  queryRow: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
  queryInput: { flex: 1, minWidth: 180, appearance: "none", background: "#040608", border: "1px solid rgba(255,255,255,0.10)", color: "#e2e8f0", padding: "9px 11px", borderRadius: 10, fontSize: 12.5, fontFamily: "'Figtree', system-ui, sans-serif" },
  primaryBtn: { appearance: "none", background: "linear-gradient(135deg, #c5a059, #ffd966)", border: "1px solid #c5a059", color: "#0c1a10", padding: "8px 14px", borderRadius: 10, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11.5, fontWeight: 800, letterSpacing: "0.06em" },
  err: { padding: "8px 12px", background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.30)", color: "#fecaca", borderRadius: 10, fontSize: 12 },
  agents: { display: "flex", flexDirection: "column", gap: 8 },
  agent: { background: "#040608", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 10, padding: 10, display: "flex", flexDirection: "column", gap: 6 },
  agentHead: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 },
  agentChip: { fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.08em", fontWeight: 800, padding: "3px 9px", border: "1px solid", borderRadius: 999 },
  offlinePill: { fontSize: 9.5, color: "#fcd34d", fontFamily: "'Space Mono', monospace", border: "1px solid #fcd34d55", borderRadius: 999, padding: "1px 7px", letterSpacing: "0.05em" },
  agentTime: { color: "#64748b", fontFamily: "'Space Mono', monospace", fontSize: 10 },
  agentBody: { margin: 0, color: "#cbd5e1", fontFamily: "'Figtree', 'Inter', system-ui, sans-serif", fontSize: 12.5, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" },
  hitlRow: { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 4 },
  approveBtn: { appearance: "none", background: "rgba(16,185,129,0.10)", border: "1px solid #10b981", color: "#10b981", padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800 },
  rejectBtn: { appearance: "none", background: "rgba(239,68,68,0.10)", border: "1px solid #ef4444", color: "#ef4444", padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800 },
  escalateBtn: { appearance: "none", background: "rgba(167,139,250,0.10)", border: "1px solid #a78bfa", color: "#a78bfa", padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800 },
  traceCode: { color: "#64748b", fontFamily: "'Space Mono', monospace", fontSize: 10, marginLeft: 4 },
};
